import { createFileRoute, Outlet, useMatches, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "@/lib/icons";
import { inPlatformConsole } from "@alm/policy";
import { PageHeadingLevel } from "@/components/app/shell/page-header";
import {
  SettingsScopeLine,
  useBuildingInView,
  useSettingsRemountKey,
} from "@/components/app/rails/settings-scope-line";
import { SettingsScopeTabs, sectionHeading } from "@/components/app/rails/settings-scope-tabs";
import { useSettingsMenu } from "@/components/app/rails/use-settings-menu";
import { Button } from "@/components/ui/button";
import { usePolicy } from "@/lib/policy";
import { useWorkspace } from "@/lib/workspace/workspace";
import { cn } from "@/lib/utils";
import { SettingsHubSection } from "./-components/account/settings-hub-section";
import { SearchField } from "@/components/app/patterns/search-field";

/** A section that needs the whole width — the roles editor is a hundred boxes beside a summary —
 * says so on its route, and the hub folds its rail away while that section is open. The breadcrumb
 * still says where you are. */
declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    fullWidth?: boolean;
  }
}

export const Route = createFileRoute("/_authenticated/_administration/settings")({
  component: SettingsHub,
});

/** The settings hub. Its rail shows one scope at a time behind tabs (the organization's settings,
 * the building in view's, yours), then the categories people know. Below md it folds behind one
 * row. */
function SettingsHub() {
  const { navigation, subject, me } = usePolicy();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [filter, setFilter] = useState("");
  /* In the Settings workspace its rail already lists every section, so the
     hub's own list beside it was the same menu twice. Every console workspace
     has its own rail too. Core has no such rail and keeps the hub as it was. */
  const workspace = useWorkspace().active;
  const inWorkspace = workspace.id === "settings" || inPlatformConsole(subject);
  const fullWidth = useMatches({
    select: (matches) => matches.some((match) => match.staticData.fullWidth === true),
  });
  const railHidden = inWorkspace || fullWidth;
  const building = useBuildingInView();
  const remountKey = useSettingsRemountKey();
  const names = { organization: me.activeOrganization?.name ?? null, building: building.name };

  const menu = useSettingsMenu(navigation, pathname, filter);
  const { here: current, tab } = menu;
  /* Open by hand until you go somewhere; a new section closes it. */
  const [openAt, setOpenAt] = useState<string | null>(null);
  const listOpen = openAt === pathname;

  /* The rail scrolls on its own beside a long page; landing on a section low
     in it (Therapies, Billing) should still show where you are. */
  const railRef = useRef<HTMLDivElement>(null);
  const currentPath = current?.item.path;
  useEffect(() => {
    const rail = railRef.current;
    const here = currentPath
      ? rail?.querySelector<HTMLElement>(`[data-current="${currentPath}"]`)
      : null;
    if (!rail || !here || rail.scrollHeight <= rail.clientHeight) return;
    const top =
      here.getBoundingClientRect().top - rail.getBoundingClientRect().top + rail.scrollTop;
    if (top < rail.scrollTop || top + here.offsetHeight > rail.scrollTop + rail.clientHeight) {
      rail.scrollTop = top - rail.clientHeight / 3;
    }
  }, [currentPath]);

  const heading = current ? sectionHeading(current.section, names) : null;
  /* Whose settings first; a category the screen's own name already starts with is said once. */
  const trail =
    current && heading
      ? current.item.label.toLowerCase().startsWith(current.category.toLowerCase())
        ? [heading.title, current.item.label]
        : [heading.title, current.category, current.item.label]
      : [];

  return (
    /* Two columns once the settings area itself is wide enough, not the window: beside the open
       app menu a 768px window leaves room for the list or the page, never both. */
    <div className="@container/settings space-y-6">
      {/* One quiet line: the hub's h1, then where you are in it, so the page's
          own title below is the largest thing on the screen. On a phone the
          picker below says the same and is the way to the others. */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
        <h1
          className={cn(
            "text-muted-foreground font-medium",
            !railHidden && "@max-3xl/settings:sr-only",
          )}
        >
          Settings
        </h1>
        <nav aria-label="Breadcrumb" className={cn(!railHidden && "@max-3xl/settings:hidden")}>
          <ol className="text-muted-foreground flex flex-wrap items-center gap-1.5">
            {trail.map((crumb, index) => (
              <li key={crumb} className="flex items-center gap-1.5">
                <ChevronRight aria-hidden className="text-faint size-3.5" />
                <span
                  aria-current={index === trail.length - 1 ? "page" : undefined}
                  className={cn(index === trail.length - 1 && "text-foreground font-medium")}
                >
                  {crumb}
                </span>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {railHidden ? null : (
        <button
          type="button"
          aria-expanded={listOpen}
          aria-controls="settings-sections"
          onClick={() => setOpenAt(listOpen ? null : pathname)}
          className="bg-card flex w-full touch-manipulation items-center gap-3 rounded-xl border px-4 py-3 text-left @3xl/settings:hidden"
        >
          <span className="min-w-0 flex-1">
            <span className="text-muted-foreground block text-xs">
              {current && heading ? `${heading.title}, ${current.category}` : "Settings"}
            </span>
            <span className="block truncate font-medium">
              {current ? current.item.label : "Choose a setting"}
            </span>
          </span>
          <ChevronDown
            className={cn(
              "text-muted-foreground size-4 shrink-0 transition-transform",
              listOpen && "rotate-180",
            )}
          />
        </button>
      )}

      <div
        className={cn(
          "grid grid-cols-[minmax(0,1fr)] gap-6",
          !railHidden && "@3xl/settings:grid-cols-[232px_minmax(0,1fr)]",
        )}
      >
        <nav
          id="settings-sections"
          aria-label="Settings sections"
          className={cn(
            "flex flex-col gap-3 @3xl/settings:sticky @3xl/settings:top-20 @3xl/settings:max-h-[calc(100dvh-10rem)] @3xl/settings:self-start",
            railHidden && "hidden",
            !railHidden && !listOpen && "@max-3xl/settings:hidden",
          )}
        >
          {/* The tabs and the search sit above the list that scrolls, so no row hides under
              them. */}
          <SettingsScopeTabs
            sections={menu.available}
            active={tab.active}
            onChoose={tab.choose}
            className="shrink-0"
          />
          <div className="shrink-0 @3xl/settings:px-0.5">
            <SearchField
              value={filter}
              onChange={setFilter}
              placeholder="Find a setting"
              className="sm:w-full"
            />
          </div>
          <div
            ref={railRef}
            className="flex min-h-0 flex-col gap-6 @3xl/settings:overflow-y-auto @3xl/settings:overscroll-contain @3xl/settings:px-0.5 @3xl/settings:pb-0.5"
          >
            {menu.shown.map((group) => (
              <SettingsHubSection
                key={group.section}
                section={group.section}
                heading={sectionHeading(group.section, names)}
                categories={group.categories}
                pathname={pathname}
                onOpen={() => setOpenAt(null)}
              />
            ))}
            {menu.searching && menu.shown.length === 0 ? (
              <p className="text-muted-foreground px-2 text-sm">
                No section matches “{filter}”.{" "}
                <Button variant="link" className="h-auto min-h-0 p-0" onClick={() => setFilter("")}>
                  Clear search
                </Button>
              </p>
            ) : null}
          </div>
        </nav>
        <div className={cn("min-w-0", listOpen && "@max-3xl/settings:hidden")}>
          <SettingsScopeLine className="mb-3" />
          <div key={remountKey} className="space-y-6">
            <PageHeadingLevel.Provider value={2}>
              <Outlet />
            </PageHeadingLevel.Provider>
          </div>
        </div>
      </div>
    </div>
  );
}
